import { Link } from 'react-router-dom'
import { Menu } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useSystemHealth } from '@/hooks/useSystemHealth'
import { useHome } from '@/hooks/useHome'
import { cn, displayPlanLabel } from '@/lib/utils'

export function AppHeader() {
  const { user } = useAuth()
  const { data: homeData } = useHome(true)
  const { data: healthData, isLoading: isHealthLoading, isError: isHealthError } = useSystemHealth(true)

  const operational = !isHealthError && healthData?.status === 'ready'
  const dotColor = isHealthLoading ? 'bg-text-dim' : operational ? 'bg-brand' : 'bg-danger'
  const statusText = isHealthLoading ? 'Conectando' : operational ? 'Online' : 'Conexión'

  // P0.55 (Sección 6/31 del encargo): nunca mostrar el rol interno ADMIN como si fuera un
  // plan comercial -- se muestra su equivalente comercial real (BLACK).
  const userPlan = displayPlanLabel(homeData?.subscription?.plan || user?.plan)

  // P0.53 (29/08/2026, Sección 6 del encargo): ELITE retirado -- nunca es un plan real
  // asignable a un cliente (`plan_tiers.py` nunca lo mapea).
  const planBadgeColors: Record<string, string> = {
    FREE: 'border-border text-text-muted bg-panel-elevated/50',
    PRO: 'border-brand/40 text-brand bg-brand-soft/40',
    BLACK: 'border-exact/50 text-exact bg-exact-soft/40 shadow-[0_0_12px_rgba(167,130,255,0.08)]',
  }

  return (
    <header className="sticky top-0 z-40 bg-black/80 backdrop-blur-md border-b border-border px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        {/* Logo circular premium */}
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-brand/20 blur-[2px]" />
          <img
            src="/logo-96.png"
            alt="John Anthony Logo"
            className="relative h-8 w-8 rounded-full border border-brand/50 object-cover shrink-0"
          />
        </div>

        {/* Título e indicador online */}
        <div>
          <h1 className="text-xs font-black tracking-wider uppercase leading-none">
            JOHN <span className="text-brand">ANTHONY</span>
          </h1>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={cn('h-1.5 w-1.5 rounded-full animate-signal shrink-0', dotColor)} />
            <span className="text-[9px] font-semibold text-text-muted uppercase tracking-wider">
              {statusText}
            </span>
          </div>
        </div>
      </div>

      {/* Plan del usuario / Indicador premium + acceso a "Más" -- P0.23 (Sección 2 del
          encargo): la barra inferior se redujo a los 5 destinos del mockup maestro
          (Inicio/Picks/Parlays/Reto/Banca), así que Marcadores Probables, Resultado Exacto,
          historial, suscripción y demás quedan accesibles desde este icono en vez de un 6º
          botón permanente abajo.

          P0.56 (Sección 4 del encargo): en escritorio el botón "Más" desaparece
          (`md:hidden`) -- `DesktopNav` ya contiene TODA la navegación (incluida Suscripción/
          Historial/Favoritos/Ayuda), así que mantenerlo visible era exactamente la
          "navegación duplicada sidebar + hamburguesa" señalada como problema. Solo
          móvil/Telegram sigue necesitando este acceso. */}
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider border',
            planBadgeColors[userPlan] || planBadgeColors.FREE,
          )}
        >
          Plan {userPlan}
        </span>
        {/* P0.56 (Sección 4 del encargo): el header debe cerrar con "plan + usuario/avatar".
            Sin foto real disponible (MiniAppUser no trae photo_url), se usa la inicial del
            nombre mostrado -- nunca se inventa una imagen. */}
        {user && (
          <div
            title={user.display_name || user.telegram_username || 'Usuario'}
            className="h-7 w-7 rounded-full bg-panel-elevated border border-border flex items-center justify-center text-[10px] font-bold text-text-muted shrink-0"
          >
            {(user.display_name || user.telegram_username || '?').charAt(0).toUpperCase()}
          </div>
        )}
        <Link
          to="/mas"
          aria-label="Más"
          className="md:hidden p-1.5 rounded-xl bg-panel-elevated border border-border text-text-dim hover:text-brand hover:border-brand/40 transition"
        >
          <Menu className="h-4 w-4" />
        </Link>
      </div>
    </header>
  )
}
